import { stringify } from "csv/browser/esm/sync";
import fileDownload from "js-file-download";

import { cubeApiUrl } from "@megarax/crm-cube";

import { ReportCellResult } from "@megaron/crm-contracts";

export const exportFormats = ["html", "pdf", "csv", "ipynb"] as const;
export type ExportFormat = (typeof exportFormats)[number];

export type ExportedReport = {
  title: string;
  description: string;
  period: { start: string; end: string };
  cells: { uuid: string; title: string | null; code: string; result: ReportCellResult | null }[];
};

const escapeHtml = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const fileName = (report: ExportedReport, extension: string) =>
  `${report.title}_${report.period.start}_${report.period.end}.${extension}`;

const pageStyles = () =>
  Array.from(document.styleSheets)
    .flatMap((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText);
      } catch {
        return [];
      }
    })
    .join("\n");

const snapshot = (content: HTMLElement) => {
  const copy = content.cloneNode(true) as HTMLElement;
  const canvases = content.querySelectorAll("canvas");
  copy.querySelectorAll("canvas").forEach((canvas, i) => {
    const image = document.createElement("img");
    image.src = canvases[i].toDataURL("image/png");
    image.setAttribute("style", "display: block; width: 100%; height: auto;");
    canvas.parentElement?.style.setProperty("height", "auto");
    canvas.replaceWith(image);
  });
  return copy.innerHTML;
};

const reportDocument = (report: ExportedReport, content: HTMLElement) => `<!doctype html>
<html lang="pl">
<head>
<meta charset="utf-8">
<title>${escapeHtml(report.title)}</title>
<style>
${pageStyles()}
body { margin: 0 auto; padding: 2rem; max-width: 1680px; background: #f6f5f3; font-family: system-ui, sans-serif; }
@page { size: A4 landscape; margin: 1cm; }
@media print { body { padding: 0; background: white; } section { break-inside: avoid; } }
</style>
</head>
<body>
<h1 style="margin: 0 0 0.25rem;">${escapeHtml(report.title)}</h1>
<p style="margin: 0;">${escapeHtml(report.description)}</p>
<p style="margin: 0.25rem 0 1.5rem; color: #6b6a68;">Okres: ${report.period.start} – ${report.period.end}</p>
${snapshot(content)}
<script>
addEventListener("message", (event) => {
  const frame = Array.from(document.querySelectorAll("iframe")).find((f) => f.contentWindow === event.source);
  if (frame && typeof event.data?.frameHeight === "number") frame.style.height = event.data.frameHeight + "px";
});
</script>
</body>
</html>`;

const downloadHtml = (report: ExportedReport, content: HTMLElement) =>
  fileDownload(reportDocument(report, content), fileName(report, "html"), "text/html;charset=utf-8");

const chartsSettleDelay = 1000;

const printPdf = (report: ExportedReport, content: HTMLElement) => {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.left = "-10000px";
  frame.style.width = "277mm";
  frame.style.height = "190mm";
  frame.style.border = "none";
  frame.onload = () => {
    const printed = frame.contentWindow;
    if (!printed) return frame.remove();
    printed.onafterprint = () => frame.remove();
    printed.document.title = fileName(report, "pdf");
    setTimeout(() => {
      printed.focus();
      printed.print();
    }, chartsSettleDelay);
  };
  frame.srcdoc = reportDocument(report, content);
  document.body.appendChild(frame);
};

const downloadCsv = (report: ExportedReport) => {
  const rows = report.cells.flatMap(({ title, result }) =>
    result && result.columns.length > 0 ? [[title ?? ""], result.columns, ...result.rows, []] : [],
  );
  fileDownload(stringify(rows, { bom: true }), fileName(report, "csv"), "text/csv;charset=utf-8");
};

const parametersCell = (report: ExportedReport) =>
  [
    "import os",
    "from getpass import getpass",
    "",
    `os.environ["REPORT_START"] = ${JSON.stringify(report.period.start)}`,
    `os.environ["REPORT_END"] = ${JSON.stringify(report.period.end)}`,
    `os.environ.setdefault("CUBE_URL", ${JSON.stringify(cubeApiUrl)})`,
    `if not os.environ.get("CUBE_AUTHORIZATION"):`,
    `    os.environ["CUBE_AUTHORIZATION"] = getpass("Token Cube: ")`,
  ].join("\n");

const tableHtml = (result: ReportCellResult) =>
  `<table><thead><tr>${result.columns.map((column) => `<th>${escapeHtml(column)}</th>`).join("")}</tr></thead><tbody>${result.rows
    .map((row) => `<tr>${row.map((value) => `<td>${escapeHtml(String(value ?? ""))}</td>`).join("")}</tr>`)
    .join("")}</tbody></table>`;

const notebookOutputs = (result: ReportCellResult | null) => {
  if (!result) return [];
  const [ename = "Error", ...evalue] = (result.error ?? "").split(": ");
  return [
    ...(result.stdout ? [{ output_type: "stream", name: "stdout", text: result.stdout }] : []),
    ...(result.stderr ? [{ output_type: "stream", name: "stderr", text: result.stderr }] : []),
    ...result.media.map((media) => ({ output_type: "display_data", data: { [media.type]: media.data }, metadata: {} })),
    ...(result.columns.length > 0
      ? [{ output_type: "display_data", data: { "text/html": tableHtml(result) }, metadata: {} }]
      : []),
    ...(result.status === "error" ? [{ output_type: "error", ename, evalue: evalue.join(": "), traceback: [] }] : []),
  ];
};

const markdownCell = (id: string, source: string) => ({ cell_type: "markdown", id, metadata: {}, source });

const codeCell = (id: string, source: string, outputs: unknown[]) => ({
  cell_type: "code",
  id,
  metadata: {},
  execution_count: null,
  source,
  outputs,
});

const downloadNotebook = (report: ExportedReport) => {
  const notebook = {
    nbformat: 4,
    nbformat_minor: 5,
    metadata: {
      kernelspec: { name: "python3", display_name: "Python 3", language: "python" },
      language_info: { name: "python" },
    },
    cells: [
      markdownCell(
        "report",
        `# ${report.title}\n\n${report.description}\n\nOkres: ${report.period.start} – ${report.period.end}`,
      ),
      codeCell("parameters", parametersCell(report), []),
      ...report.cells.flatMap((cell) => [
        ...(cell.title ? [markdownCell(`${cell.uuid}-title`, `## ${cell.title}`)] : []),
        codeCell(cell.uuid, cell.code, notebookOutputs(cell.result)),
      ]),
    ],
  };
  fileDownload(JSON.stringify(notebook, null, 1), fileName(report, "ipynb"), "application/x-ipynb+json");
};

export const exportReport = (format: ExportFormat, report: ExportedReport, content: HTMLElement) => {
  if (format === "html") return downloadHtml(report, content);
  if (format === "pdf") return printPdf(report, content);
  if (format === "ipynb") return downloadNotebook(report);
  return downloadCsv(report);
};
